import { FC, useMemo, useRef } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { StoreShipmentInterface } from '../../interfaces'
import { PrimaryButton, SecondaryButton } from '../Atoms/Buttons'
import { ModalWidthFull } from '../Atoms/ModalWidthFull'
import {
  ShipmentLabelFormat,
  useShipmentLabel,
} from '../../hooks/useShipmentLabel'
import { printHtmlContent, printIframeContent } from '../../utils/print'
import { alertService } from '../../services/alertService'

interface ShipmentLabelDjangoModalProps {
  open: boolean
  shipment: StoreShipmentInterface[]
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  format?: ShipmentLabelFormat
}

export const ShipmentLabelDjangoModal: FC<ShipmentLabelDjangoModalProps> = ({
  open,
  shipment,
  setOpen,
  format = 'pdf',
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const identifications = useMemo(
    () => shipment.map((item) => item.shipmentNumber),
    [shipment]
  )

  const { loading, error, htmlContent, pdfUrl } = useShipmentLabel(
    identifications,
    format,
    open
  )

  const handlePrint = () => {
    if (htmlContent) {
      printHtmlContent(htmlContent, {
        title: `Etiqueta ${identifications.join(', ')}`,
      }).catch((error: any) =>
        alertService.error(
          'No se pudo imprimir la etiqueta.',
          error?.message ?? ''
        )
      )
      return
    }

    if (!printIframeContent(iframeRef.current)) {
      alertService.error('No se pudo imprimir la etiqueta.')
    }
  }

  return (
    <ModalWidthFull
      open={open}
      setOpen={setOpen}
      className="flex flex-1 mx-4 my-8"
      style={{ maxWidth: '90rem', height: '50rem' }}
    >
      <div className="flex flex-1 flex-col">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">
            <span className="text-gray-700 hidden sm:inline">
              Impresión de etiqueta:{' '}
            </span>
          </h2>

          <div className="ml-3 flex h-7 items-center">
            <button
              type="button"
              className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-main-500"
              onClick={() => setOpen(false)}
            >
              <span className="absolute -inset-2.5" />
              <span className="sr-only">Close panel</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>

        <hr className="border-gray-300 my-3" />

        <div className="flex flex-1 items-center justify-center overflow-hidden">
          {loading && (
            <p className="text-lg text-gray-600">Generando etiqueta...</p>
          )}
          {!loading && error && <p className="text-lg text-red-600">{error}</p>}
          {!loading && !error && htmlContent && (
            <iframe
              ref={iframeRef}
              title="Etiqueta"
              srcDoc={htmlContent}
              width="100%"
              height="100%"
            />
          )}
          {!loading && !error && pdfUrl && (
            <iframe
              ref={iframeRef}
              title="Etiqueta"
              src={pdfUrl}
              width="100%"
              height="100%"
            />
          )}
        </div>

        <hr className="border-gray-300 my-6" />

        <div className="flex justify-between items-center">
          <SecondaryButton onClick={() => setOpen(false)}>
            <span className="w-20">Cerrar</span>
          </SecondaryButton>
          <PrimaryButton
            onClick={handlePrint}
            disabled={loading || !!error || (!htmlContent && !pdfUrl)}
          >
            <span className="w-20">Imprimir</span>
          </PrimaryButton>
        </div>
      </div>
    </ModalWidthFull>
  )
}
